import { type PowerSyncDrizzleDB, user } from "@aula/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL the mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) used to have this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in guest mode the UPDATE went out **without `WHERE`** → rewrote ALL
 * `user` rows in the local database.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during a guest→real upgrade the
 * guest database CONNECTS to the real account's connector and
 * auto-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched row
 * queues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider` seeds the guest row
 * (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME
 * row that the unfiltered branch intended to touch when the table had only one
 * row—identical behavior in the normal case, bounded in the rest. This is also
 * the form already used by `updateUserFontFamilyMutation`,
 * `updateUserImageMutation` and `updateLeaderboardOptInMutation`, which never
 * had a guest branch.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Why ALL also pass through `patchRowVerified` (§4.11) ────────────────
 * updated 2026-08-13 after the `ThemeProvider` seeding guard (see below)
 * —the conclusion did NOT change, only the reason it remains true.
 *
 * `user` cannot be assumed to exist just because "the server creates it
 * on registration". Although the server DOES create the row unconditionally during
 * alta (`packages/core-rust/src/db/user.rs:101,350` — `INSERT INTO "user"`),
 * that does not close the window:
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as the local
 *     SQLite opens, not when the initial sync finishes.
 *   - `userId()` becomes active as soon as `signUpActor`/`signInActor`
 *     (`packages/shared/src/auth/controllers.ts:273,337`) receive the session
 *     from the server—an independent REST call from the PowerSync stream.
 *
 * Between the time `db`+`userId()` are ready and the initial checkpoint brings the
 * server-created `user` row, there is a window in which a normal `UPDATE`
 * (change language, theme…) targets a row the local view does not yet have—it
 * matches ZERO rows and silently returns "success" (see
 * `powersync/verified-write.ts`). This window does not depend on anyone seeding
 * a local row: it is the view syncing, nothing more.
 *
 * (Historical context, now CLOSED) Before the guard commit there was a THIRD
 * factor that WORSENED this same window, without causing it: `createEffect`
 * in `ThemeProvider` above locally inserted a "ghost" `user` row
 * a 3-column "ghost" `user` row for ANY `uid`, without distinguishing guest from real account,
 * competing with the real row the server had already created and that was on
 * its way via sync (which is why it caught `UNIQUE constraint failed`: sometimes the local
 * INSERT won the race, sometimes the server). The `uid !==
 * VISITOR_ID` guard (`apps/web/src/providers/theme-provider.tsx`) closed that specific
 * factor: the effect now only seeds for the guest. **This does NOT make**
 * the `patchRowVerified` protection on `user` unnecessary**—the two
 * points above (`db` and `userId()` ready before the checkpoint) still
 * open the same window for any early write, with or without a
 * phantom row in the mix. This is generic defense in depth for the
 * checkpoint, not a patch left over from the already-closed bug—if one day
 * it is removed, that should be because the window above is proven gone,
 * not because `ThemeProvider` stopped seeding phantoms. Same criterion as
 * `habit-streak-app`; see the full measurement in
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (just like language, font and theme) and not via REST: this way
 * the new photo is visible immediately and survives a cold reload without a
 * connection. The BINARY does go via REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (initials return).
 *
 * The guest has no synchronizable row of their own and cannot have an avatar (zero
 * rule), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}

/**
 * Consent to appear on leaderboards (privacy opt-in).
 *
 * Writes `user.leaderboard_opt_in` local-first; the server write path
 * reconciles the user's leaderboard entries (opt-out hides them from other users'
 * streams; opt-in publishes them with their current XP). Server-authoritative:
 * the client never writes `leaderboard_entry` directly.
 */
export async function updateLeaderboardOptInMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  optIn: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { leaderboard_opt_in: optIn ? 1 : 0 }, options);
}
